import * as React from 'react';

import Container from '@mui/material/Container';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';

import { useLoaderData, useNavigate } from 'react-router-dom';

import { useForm, FormProvider } from 'react-hook-form';
import Form from '../components/form/Form';
import FormTextField from '../components/form/FormTextField';
import FormDropDown from '../components/form/FormDropDown';
import FormNumberTextField from '../components/form/FormNumberTextField';
import FormButton from '../components/form/FormButton';
import FormObjectArrayList from '../components/form/FormObjectArrayList';
import FormSwitch from '../components/form/FormSwitch';

import Header from '../components/Header';
import GoBack from '../components/GoBack';

import { newAssessmentType, getAssessmentType, saveAssessmentType } from '../models/assessmentTypes';
import { getFacility } from '../models/facility';
import { useAuth } from '../context/AuthProvider';
import { useMessage } from '../context/MessageProvider';
import { useConnectivity } from '../context/ConnectivityProvider';

import { schema } from '../models/biologySchema';

export async function loader({ params }) {
  const assessmentType = params?.assessmentTypeId
    ? await getAssessmentType(params.assessmentTypeId)
    : newAssessmentType();

  const facility = await getFacility();
  return { assessmentType: assessmentType || newAssessmentType(), facility };
}

const resolveSpeciesRow = (speciesValue) => {
  if (!speciesValue) {
    return { phylum: '', classes: '', order: '', genus: '', species: '' };
  }

  for (const phylum of schema) {
    for (const classes of phylum.class || []) {
      for (const order of classes.order || []) {
        for (const genus of order.genus || []) {
          for (const species of genus.species || []) {
            if (species.scientificName === speciesValue) {
              return {
                phylum: phylum.scientificName,
                classes: classes.scientificName,
                order: order.scientificName,
                genus: genus.scientificName,
                species: speciesValue,
              };
            }
          }
        }
      }
    }
  }

  return { phylum: '', classes: '', order: '', genus: '', species: speciesValue };
};

const toLabelOption = (cur) => ({
  value: cur.scientificName,
  label: `${cur.commonName} (${cur.scientificName})`,
});

export default function AssessmentTypeFormPage({ title = 'Assessment Type', ...props }) {
  const navigate = useNavigate();
  const { assessmentType, facility } = useLoaderData();
  const { facilityId } = useAuth();
  const { isOnline } = useConnectivity();
  const { setSuccess, setError } = useMessage();
  const [working, setWorking] = React.useState(false);

  const species_list = (assessmentType.species || []).map((value) => resolveSpeciesRow(value));
  const isNew = !assessmentType?.id;
  const facilityTimeframes = (facility?.workday_timeframes?.length)
    ? facility.workday_timeframes
    : (facility?.start_of_day && facility?.end_of_day)
      ? [{ start_of_day: facility.start_of_day, end_of_day: facility.end_of_day }]
      : [];
  const formatTimeframeLabel = (timeframe, index) => {
    const startLabel = timeframe?.start_of_day?.format ? timeframe.start_of_day.format('HH:mm') : '';
    const endLabel = timeframe?.end_of_day?.format ? timeframe.end_of_day.format('HH:mm') : '';
    if (startLabel && endLabel) return `${startLabel} - ${endLabel}`;
    return `Timeframe ${index + 1}`;
  };
  const timeframeOptions = [
    { value: '', label: 'No preference' },
    ...facilityTimeframes.map((cur, index) => ({
      value: String(index),
      label: formatTimeframeLabel(cur, index),
    })),
  ];
  const defaultValues = {
    ...assessmentType,
    species_list,
    section_a_observations_total: isNew
      ? facility?.section_a_observations_total ?? null
      : assessmentType.section_a_observations_total,
    section_b_observations_total: isNew
      ? facility?.section_b_observations_total ?? null
      : assessmentType.section_b_observations_total,
    section_a_timer_seconds: isNew ? 60 : assessmentType.section_a_timer_seconds,
    section_b_timer_seconds: isNew ? 600 : assessmentType.section_b_timer_seconds,
    allow_single_open_assessment_per_animal: isNew
      ? assessmentType.allow_single_open_assessment_per_animal ?? false
      : assessmentType.allow_single_open_assessment_per_animal,
  };
  const methods = useForm({ defaultValues });

  const phylumOptions = schema.map((cur) => toLabelOption(cur));

  const getClassesList = (phylumValue) =>
    schema.find((cur) => cur.scientificName === phylumValue)?.class || [];
  const getOrderList = (classesList, classesValue) =>
    classesList.find((cur) => cur.scientificName === classesValue)?.order || [];
  const getGenusList = (orderList, orderValue) =>
    orderList.find((cur) => cur.scientificName === orderValue)?.genus || [];
  const getSpeciesList = (genusList, genusValue) =>
    genusList.find((cur) => cur.scientificName === genusValue)?.species || [];

  const onSubmit = async (data) => {
    try {
      setWorking(true);

      const payload = { ...assessmentType, ...data };
      payload.species = (data.species_list || [])
        .map((item) => item?.species)
        .filter((value) => !!value);
      delete payload.species_list;

      await saveAssessmentType(payload, facilityId, isOnline);
      setSuccess('Assessment Type Saved');
      navigate('/assessment-types');
    } catch (error) {
      console.error(error);
      setError(error);
    } finally {
      setWorking(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <GoBack onClick={() => navigate('/assessment-types')} />
      <Header label={title} />

      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid xs={12}>
              <FormTextField name="name" label="Name" required />
            </Grid>

            <Grid xs={12}>
              <FormTextField name="description" label="Description" multiline minRows={2} />
            </Grid>

            <Grid xs={12}>
              <FormTextField
                name="default_assessment_title"
                label="Default Assessment Title"
                helperText="Supports {{animal}}, {{species}}, {{user}}, and {{date}}"
              />
            </Grid>

            <Grid xs={12}>
              <FormDropDown
                name="preferred_timeframe_index"
                label="Preferred Workday Timeframe"
                list={timeframeOptions}
              />
            </Grid>

            <Grid xs={12}>
              <Typography variant="subtitle1">Species Filter</Typography>
            </Grid>

            <Grid xs={12}>
              <FormObjectArrayList
                name="species_list"
                label="Allowed Species"
                showRowIndex={false}
                buttonText="Add Species"
                emptyText="No species restrictions."
                newRow={() => ({ phylum: '', classes: '', order: '', genus: '', species: '' })}
                fields={[
                  {
                    key: 'phylum',
                    label: 'Phylum',
                    component: TextField,
                    options: phylumOptions,
                    componentProps: { required: true },
                    gridProps: { xs: 12 },
                  },
                  {
                    key: 'classes',
                    label: 'Class',
                    component: TextField,
                    options: (row) => getClassesList(row?.phylum || '').map((cur) => toLabelOption(cur)),
                    componentProps: (row) => ({ required: true, disabled: !(row?.phylum) }),
                    gridProps: { xs: 12 },
                  },
                  {
                    key: 'order',
                    label: 'Order',
                    component: TextField,
                    options: (row) => {
                      const classesList = getClassesList(row?.phylum || '');
                      return getOrderList(classesList, row?.classes || '').map((cur) => toLabelOption(cur));
                    },
                    componentProps: (row) => ({ required: true, disabled: !(row?.classes) }),
                    gridProps: { xs: 12 },
                  },
                  {
                    key: 'genus',
                    label: 'Genus',
                    component: TextField,
                    options: (row) => {
                      const classesList = getClassesList(row?.phylum || '');
                      const orderList = getOrderList(classesList, row?.classes || '');
                      return getGenusList(orderList, row?.order || '').map((cur) => toLabelOption(cur));
                    },
                    componentProps: (row) => ({ required: true, disabled: !(row?.order) }),
                    gridProps: { xs: 12 },
                  },
                  {
                    key: 'species',
                    label: 'Species',
                    component: TextField,
                    options: (row) => {
                      const classesList = getClassesList(row?.phylum || '');
                      const orderList = getOrderList(classesList, row?.classes || '');
                      const genusList = getGenusList(orderList, row?.order || '');
                      return getSpeciesList(genusList, row?.genus || '').map((cur) => toLabelOption(cur));
                    },
                    componentProps: (row) => ({ required: true, disabled: !(row?.genus) }),
                    gridProps: { xs: 12 },
                  },
                ]}
              />
            </Grid>

            <Grid xs={12}>
              <Typography variant="subtitle1">Defaults</Typography>
            </Grid>

            <Grid xs={12} sm={6}>
              <FormNumberTextField
                name="section_a_observations_total"
                label="Section A Observations Total"
                min={4}
                max={12}
              />
            </Grid>

            <Grid xs={12} sm={6}>
              <FormNumberTextField
                name="section_b_observations_total"
                label="Section B Observations Total"
                min={4}
                max={12}
              />
            </Grid>

            <Grid xs={12} sm={6}>
              <FormNumberTextField
                name="section_a_timer_seconds"
                label="Section A Timer (seconds)"
                min={1}
              />
            </Grid>

            <Grid xs={12} sm={6}>
              <FormNumberTextField
                name="section_b_timer_seconds"
                label="Section B Timer (seconds)"
                min={1}
              />
            </Grid>

            <Grid xs={12}>
              <FormSwitch
                name="allow_single_open_assessment_per_animal"
                label="Allow only a single open assessment per animal"
              />
            </Grid>

            <Grid xs={12}>
              <FormButton working={working}>Save Assessment Type</FormButton>
            </Grid>
          </Grid>
        </Form>
      </FormProvider>
    </Container>
  );
}
